import { WhichWeightAPI } from 'WhichWeightAPI'
import { useState, useEffect, useMemo } from 'react'
import { ArrowTopRightOnSquareIcon, ChevronDownIcon, ChevronUpIcon, ClipboardDocumentIcon } from '@heroicons/react/20/solid'
import AppleAppStoreIcon from 'media/icons/app_store/apple_app_store.png'
import GooglePlayStoreIcon from 'media/icons/app_store/google_play_store.png'
import moment from 'moment'

import ScreenFrame from 'components/Frame/ScreenFrame'
import OutlineButton from 'components/Buttons/OutlineButton'
import SearchBar from 'components/Search/SearchBar'
import PaginationController from 'components/Pagination/PaginationController'

const ANDROID_NOTIFICATION_TYPES = {
    1: 'SUBSCRIPTION_RECOVERED',
    2: 'SUBSCRIPTION_RENEWED',
    3: 'SUBSCRIPTION_CANCELED',
    4: 'SUBSCRIPTION_PURCHASED',
    5: 'SUBSCRIPTION_ON_HOLD',
    6: 'SUBSCRIPTION_IN_GRACE_PERIOD',
    7: 'SUBSCRIPTION_RESTARTED',
    8: 'SUBSCRIPTION_PRICE_CHANGE_CONFIRMED',
    9: 'SUBSCRIPTION_DEFERRED',
    10: 'SUBSCRIPTION_PAUSED',
    11: 'SUBSCRIPTION_PAUSE_SCHEDULE_CHANGED',
    12: 'SUBSCRIPTION_REVOKED',
    13: 'SUBSCRIPTION_EXPIRED',
    19: 'SUBSCRIPTION_PRICE_CHANGE_UPDATED',
    20: 'SUBSCRIPTION_PENDING_PURCHASE_CANCELED',
    22: 'SUBSCRIPTION_PRICE_STEP_UP_CONSENT_UPDATED',
}

const IOS_STATUS_LABELS = {
    1: 'Active',
    2: 'Expired',
    3: 'Billing Retry',
    4: 'Billing Grace Period',
    5: 'Revoked',
}

function FormatApplePrice(price, currency = 'USD') {
    if (price == null || price === '') return '—'
    const amount = Number(price) / 1000
    if (Number.isNaN(amount)) return '—'
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency || 'USD',
        }).format(amount)
    } catch {
        return `${amount.toFixed(2)} ${currency || ''}`.trim()
    }
}

function FormatEpochMs(ms) {
    if (ms == null || ms === '') return '—'
    const n = Number(ms)
    if (Number.isNaN(n)) return '—'
    return moment.utc(n).local().format('MMM D, YYYY h:mm A')
}

function FormatDbTimestamp(ts) {
    if (!ts) return '—'
    return moment.utc(ts).local().format('MMM D, YYYY h:mm A')
}

function MaskSensitiveToken(token) {
    if (!token || typeof token !== 'string') return '—'
    if (token.length <= 16) return `${token.slice(0, 4)}…${token.slice(-4)}`
    return `${token.slice(0, 8)}…${token.slice(-8)}`
}

function CopyToClipboard(text) {
    if (!text) return
    navigator.clipboard.writeText(String(text))
}

function GetIosNotificationSummary(payload) {
    const data = payload?.data ?? {}
    const tx = data.decodedTransactionInfo ?? {}
    const renewal = data.decodedRenewalInfo ?? {}
    const price = tx.price ?? renewal.renewalPrice
    const currency = tx.currency ?? renewal.currency ?? 'USD'

    return {
        eventLabel: [payload?.notificationType, payload?.subtype].filter(Boolean).join(' · '),
        environment: data.environment ?? tx.environment ?? renewal.environment,
        status: data.status,
        productId: tx.productId ?? renewal.productId,
        transactionId: tx.transactionId,
        originalTransactionId: tx.originalTransactionId ?? renewal.originalTransactionId,
        webOrderLineItemId: tx.webOrderLineItemId,
        subscriptionGroupIdentifier: tx.subscriptionGroupIdentifier,
        priceFormatted: FormatApplePrice(price, currency),
        currency,
        purchaseDate: FormatEpochMs(tx.purchaseDate),
        expiresDate: FormatEpochMs(tx.expiresDate),
        renewalDate: FormatEpochMs(renewal.renewalDate),
        autoRenewStatus:
            renewal.autoRenewStatus === 1
                ? 'On'
                : renewal.autoRenewStatus === 0
                  ? 'Off'
                  : renewal.autoRenewStatus != null
                    ? String(renewal.autoRenewStatus)
                    : '—',
        storefront: tx.storefront,
        transactionReason: tx.transactionReason,
        offerType: tx.offerType,
        offerDiscountType: tx.offerDiscountType,
    }
}

const IOS_INACTIVE_NOTIFICATION_TYPES = new Set([
    'EXPIRED',
    'REVOKE',
    'REFUND',
    'GRACE_PERIOD_EXPIRED',
    'DID_FAIL_TO_RENEW',
])

const IOS_ACTIVE_NOTIFICATION_TYPES = new Set([
    'SUBSCRIBED',
    'DID_RENEW',
    'RENEWAL_EXTENDED',
    'DID_CHANGE_RENEWAL_PREF',
    'OFFER_REDEEMED',
    'REFUND_REVERSED',
    'RENEWAL_EXTENSION',
])

// App Store charges (purchase/renewal). Status webhooks echo the same txn price — not new charges.
const IOS_CHARGE_NOTIFICATION_TYPES = new Set([
    'SUBSCRIBED',
    'DID_RENEW',
    'OFFER_REDEEMED',
    'REFUND_REVERSED',
    'RENEWAL_EXTENSION',
])

function IsIosChargeNotification(payload) {
    return IOS_CHARGE_NOTIFICATION_TYPES.has(payload?.notificationType)
}

const ANDROID_INACTIVE_NOTIFICATION_TYPES = new Set([
    'SUBSCRIPTION_CANCELED',
    'SUBSCRIPTION_EXPIRED',
    'SUBSCRIPTION_REVOKED',
    'SUBSCRIPTION_ON_HOLD',
    'SUBSCRIPTION_PAUSED',
])

const ANDROID_ACTIVE_NOTIFICATION_TYPES = new Set([
    'SUBSCRIPTION_PURCHASED',
    'SUBSCRIPTION_RENEWED',
    'SUBSCRIPTION_RECOVERED',
    'SUBSCRIPTION_RESTARTED',
    'SUBSCRIPTION_IN_GRACE_PERIOD',
    'SUBSCRIPTION_DEFERRED',
])

function IsIosTrialing(payload, summary) {
    const tx = payload?.data?.decodedTransactionInfo ?? {}

    if (tx.offerDiscountType === 'FREE_TRIAL') return true
    if (tx.transactionReason === 'FREE_TRIAL') return true
    if (summary?.transactionReason === 'FREE_TRIAL') return true
    if (payload?.subtype === 'INITIAL_BUY' && Number(tx.price) === 0) return true

    const expiresMs = Number(tx.expiresDate)
    if (
        !Number.isNaN(expiresMs) &&
        expiresMs > Date.now() &&
        Number(tx.price) === 0 &&
        payload?.notificationType === 'SUBSCRIBED'
    ) {
        return true
    }

    return false
}

function GetPurchaseSubscriptionStatus(purchase) {
    const notifications = purchase.appStoreServerNotifications ?? []

    if (notifications.length === 0) return 'inactive'

    const latest = notifications[0]

    if (purchase.platform === 'ios') {
        const payload = latest.payload ?? {}
        const summary = GetIosNotificationSummary(payload)
        const tx = payload?.data?.decodedTransactionInfo ?? {}
        const expiresMs = Number(tx.expiresDate)
        const hasFutureExpiry = !Number.isNaN(expiresMs) && expiresMs > Date.now()

        if (IsIosTrialing(payload, summary) && hasFutureExpiry) return 'trialing'

        if (summary.status === 3 || summary.status === 4) return 'active'

        if (
            IOS_INACTIVE_NOTIFICATION_TYPES.has(payload.notificationType) ||
            summary.status === 2 ||
            summary.status === 5 ||
            (!Number.isNaN(expiresMs) && expiresMs <= Date.now())
        ) {
            return 'inactive'
        }

        if (
            summary.status === 1 ||
            IOS_ACTIVE_NOTIFICATION_TYPES.has(payload.notificationType) ||
            hasFutureExpiry
        ) {
            return 'active'
        }

        return 'inactive'
    }

    if (purchase.platform === 'android') {
        const summary = GetAndroidNotificationSummary(latest.payload)
        const eventLabel = summary.eventLabel

        if (ANDROID_INACTIVE_NOTIFICATION_TYPES.has(eventLabel)) return 'inactive'
        if (ANDROID_ACTIVE_NOTIFICATION_TYPES.has(eventLabel)) return 'active'

        return 'inactive'
    }

    return 'inactive'
}

function SubscriptionStatusTag({ status }) {
    const tagConfig = {
        active: { label: 'Active', className: 'text-green-700' },
        inactive: { label: 'Inactive', className: 'text-red-700' },
        trialing: { label: 'Trialing', className: 'text-blue-700' },
    }

    const config = tagConfig[status] ?? tagConfig.inactive

    return (
        <span className={`text-xs font-bold uppercase tracking-wide ${config.className}`}>
            {config.label}
        </span>
    )
}

function BuildIosChargeEventDisplayMap(notifications) {
    const displayByNotificationId = new Map()

    const chargeNotifications = [...notifications]
        .filter((n) => IsIosChargeNotification(n.payload))
        .sort(
            (a, b) =>
                moment.utc(a.createdTimestamp).valueOf() -
                moment.utc(b.createdTimestamp).valueOf()
        )

    const seenTransactionIds = new Set()
    const seenNotificationUuids = new Set()

    for (const notification of chargeNotifications) {
        const tx = notification.payload?.data?.decodedTransactionInfo ?? {}
        const transactionId = tx.transactionId
        const notificationUUID = notification.payload?.notificationUUID

        const isDuplicateTransaction =
            transactionId != null && seenTransactionIds.has(transactionId)
        const isDuplicateNotification =
            notificationUUID != null && seenNotificationUuids.has(notificationUUID)

        if (isDuplicateTransaction || isDuplicateNotification) {
            displayByNotificationId.set(notification.id, {
                showPrice: false,
                duplicateLabel: 'Duplicate webhook',
            })
            continue
        }

        if (transactionId != null) seenTransactionIds.add(transactionId)
        if (notificationUUID != null) seenNotificationUuids.add(notificationUUID)

        displayByNotificationId.set(notification.id, {
            showPrice: true,
            duplicateLabel: null,
        })
    }

    return displayByNotificationId
}

function GetPurchaseTotalSubscriptionDollars(purchase) {
    const notifications = purchase.appStoreServerNotifications ?? []

    if (purchase.platform !== 'ios' || notifications.length === 0) {
        return {
            formatted: '—',
            hasAmount: false,
            uniqueTransactionCount: 0,
            chargeWebhookCount: 0,
            duplicateChargeWebhookCount: 0,
        }
    }

    let notificationsToSum = notifications.filter((n) =>
        IsIosChargeNotification(n.payload)
    )
    if (notificationsToSum.length === 0) {
        notificationsToSum = notifications
    }

    const chargeDisplayMap = BuildIosChargeEventDisplayMap(notifications)
    const countedTransactionIds = new Set()
    let totalMilliunits = 0
    let currency = 'USD'

    for (const notification of notificationsToSum) {
        const tx = notification.payload?.data?.decodedTransactionInfo ?? {}
        const renewal = notification.payload?.data?.decodedRenewalInfo ?? {}
        const price = Number(tx.price ?? renewal.renewalPrice)

        if (Number.isNaN(price) || price <= 0) continue

        const dedupeKey = tx.transactionId ?? `notification-${notification.id}`
        if (countedTransactionIds.has(dedupeKey)) continue
        countedTransactionIds.add(dedupeKey)

        totalMilliunits += price
        currency = tx.currency ?? renewal.currency ?? currency
    }

    const chargeWebhookCount = notifications.filter((n) =>
        IsIosChargeNotification(n.payload)
    ).length
    const duplicateChargeWebhookCount = [...chargeDisplayMap.values()].filter(
        (entry) => entry.duplicateLabel != null
    ).length

    return {
        formatted:
            totalMilliunits > 0 ? FormatApplePrice(totalMilliunits, currency) : '—',
        hasAmount: totalMilliunits > 0,
        uniqueTransactionCount: countedTransactionIds.size,
        chargeWebhookCount,
        duplicateChargeWebhookCount,
    }
}

function GetAndroidNotificationSummary(payload) {
    const decoded = payload?.message?.decodedData ?? {}
    const sub = decoded.subscriptionNotification ?? {}
    const typeCode = sub.notificationType

    return {
        eventLabel:
            ANDROID_NOTIFICATION_TYPES[typeCode] ??
            (typeCode != null ? `TYPE_${typeCode}` : 'UNKNOWN'),
        environment: null,
        packageName: decoded.packageName,
        subscriptionId: sub.subscriptionId,
        purchaseTokenMasked: MaskSensitiveToken(sub.purchaseToken),
        purchaseToken: sub.purchaseToken,
        eventTime: FormatEpochMs(decoded.eventTimeMillis),
        messageId: payload?.message?.messageId ?? payload?.message?.message_id,
        priceFormatted: '—',
    }
}

function SanitizePayloadForDisplay(payload, platform) {
    const clone = JSON.parse(JSON.stringify(payload ?? {}))

    if (platform === 'android') {
        const token =
            clone?.message?.decodedData?.subscriptionNotification?.purchaseToken
        if (token) {
            clone.message.decodedData.subscriptionNotification.purchaseToken =
                MaskSensitiveToken(token)
        }
    }

    if (clone.subscription) {
        clone.subscription = '[redacted]'
    }

    return clone
}

function DetailRow({ label, value, copyValue, mono = false }) {
    if (value == null || value === '' || value === '—') return null

    return (
        <div className="flex flex-col sm:flex-row sm:gap-2 py-1 border-b border-gray-100 last:border-0">
            <div className="text-xs font-medium text-gray-500 sm:w-44 shrink-0">{label}</div>
            <div className={`text-xs text-gray-900 break-all flex-1 ${mono ? 'font-mono' : ''}`}>
                {value}
                {copyValue != null && (
                    <button
                        type="button"
                        className="ml-2 inline-flex text-gray-400 hover:text-gray-700"
                        title={`Copy ${label}`}
                        onClick={() => CopyToClipboard(copyValue)}
                    >
                        <ClipboardDocumentIcon className="h-4 w-4" />
                    </button>
                )}
            </div>
        </div>
    )
}

function NotificationCard({
    notification,
    platform,
    defaultExpanded = false,
    chargeDisplay = null,
}) {
    const [expanded, setExpanded] = useState(defaultExpanded)
    const [showRawJson, setShowRawJson] = useState(false)

    const summary =
        platform === 'ios'
            ? GetIosNotificationSummary(notification.payload)
            : GetAndroidNotificationSummary(notification.payload)

    const sanitizedJson = SanitizePayloadForDisplay(notification.payload, platform)
    const showEventChargeAmount =
        chargeDisplay?.showPrice === true && summary.priceFormatted !== '—'

    return (
        <div className="border border-gray-200 rounded-md bg-white overflow-hidden">
            <button
                type="button"
                className="w-full px-3 py-2 flex items-center gap-3 text-left hover:bg-gray-50"
                onClick={() => setExpanded((v) => !v)}
            >
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                        {summary.eventLabel || 'Notification'}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                        <span>#{notification.id}</span>
                        <span>{FormatDbTimestamp(notification.createdTimestamp)}</span>
                        {summary.productId && (
                            <span className="truncate max-w-[240px]" title={summary.productId}>
                                {summary.productId}
                            </span>
                        )}
                    </div>
                </div>
                {chargeDisplay?.duplicateLabel && (
                    <span
                        className="text-xxs font-medium text-amber-700 shrink-0"
                        title="Same transactionId or notificationUUID already counted in Total charged"
                    >
                        {chargeDisplay.duplicateLabel}
                    </span>
                )}
                {showEventChargeAmount && (
                    <div
                        className="text-sm font-semibold text-gray-900 shrink-0"
                        title="Unique App Store transaction charge"
                    >
                        {summary.priceFormatted}
                    </div>
                )}
                {expanded ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-400 shrink-0" />
                ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-400 shrink-0" />
                )}
            </button>

            {expanded && (
                <div className="px-3 pb-3 border-t border-gray-200 bg-gray-50">
                    <div className="pt-2 space-y-0">
                        {platform === 'ios' && (
                            <>
                                <DetailRow
                                    label="Original transaction ID"
                                    value={summary.originalTransactionId}
                                    copyValue={summary.originalTransactionId}
                                    mono
                                />
                                <DetailRow
                                    label="Transaction ID"
                                    value={summary.transactionId}
                                    copyValue={summary.transactionId}
                                    mono
                                />
                                <DetailRow
                                    label="Web order line item ID"
                                    value={summary.webOrderLineItemId}
                                    copyValue={summary.webOrderLineItemId}
                                    mono
                                />
                                <DetailRow
                                    label="Product ID"
                                    value={summary.productId}
                                    copyValue={summary.productId}
                                    mono
                                />
                                <DetailRow
                                    label="Subscription group"
                                    value={summary.subscriptionGroupIdentifier}
                                    mono
                                />
                                {showEventChargeAmount && (
                                    <DetailRow label="Price charged" value={summary.priceFormatted} />
                                )}
                                <DetailRow label="Environment" value={summary.environment} />
                                <DetailRow
                                    label="Status"
                                    value={
                                        summary.status != null
                                            ? `${IOS_STATUS_LABELS[summary.status] ?? summary.status} (${summary.status})`
                                            : null
                                    }
                                />
                                <DetailRow label="Auto-renew" value={summary.autoRenewStatus} />
                                <DetailRow label="Storefront" value={summary.storefront} />
                                <DetailRow label="Purchase date" value={summary.purchaseDate} />
                                <DetailRow label="Expires" value={summary.expiresDate} />
                                <DetailRow label="Renewal date" value={summary.renewalDate} />
                                <DetailRow
                                    label="Transaction reason"
                                    value={summary.transactionReason}
                                />
                                <DetailRow label="Offer type" value={summary.offerType} />
                                <DetailRow
                                    label="Offer discount"
                                    value={summary.offerDiscountType}
                                />
                            </>
                        )}
                        {platform === 'android' && (
                            <>
                                <DetailRow label="Package" value={summary.packageName} mono />
                                <DetailRow
                                    label="Subscription ID"
                                    value={summary.subscriptionId}
                                    mono
                                />
                                <DetailRow
                                    label="Purchase token"
                                    value={summary.purchaseTokenMasked}
                                    copyValue={summary.purchaseToken}
                                    mono
                                />
                                <DetailRow label="Event time" value={summary.eventTime} />
                                <DetailRow label="Message ID" value={summary.messageId} mono />
                                <DetailRow
                                    label="Price"
                                    value="Not in RTDN payload"
                                />
                            </>
                        )}
                    </div>

                    <div className="mt-3 pt-2 border-t border-gray-200">
                        <button
                            type="button"
                            className="text-xs text-orange-600 hover:text-orange-800 font-medium"
                            onClick={() => setShowRawJson((v) => !v)}
                        >
                            {showRawJson ? 'Hide' : 'View'} raw JSON
                            {platform === 'android' && ' (purchase token masked)'}
                        </button>
                        {showRawJson && (
                            <pre className="mt-2 p-2 text-xxs bg-gray-900 text-gray-100 rounded overflow-auto max-h-80 font-mono">
                                {JSON.stringify(sanitizedJson, null, 2)}
                            </pre>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

function PurchaseSummaryFields({ purchase, latestIosSummary }) {
    if (purchase.platform === 'ios') {
        return (
            <>
                <DetailRow
                    label="Transaction ID"
                    value={purchase.transactionId}
                    copyValue={purchase.transactionId}
                    mono
                />
                <DetailRow
                    label="Original transaction ID"
                    value={latestIosSummary?.originalTransactionId}
                    copyValue={latestIosSummary?.originalTransactionId}
                    mono
                />
                <DetailRow label="Latest product" value={latestIosSummary?.productId} mono />
                <DetailRow label="Environment" value={latestIosSummary?.environment} />
            </>
        )
    }

    return (
        <DetailRow
            label="Purchase token"
            value={MaskSensitiveToken(purchase.purchaseToken)}
            copyValue={purchase.purchaseToken}
            mono
        />
    )
}

export default function AppStoreSubscriptionDirectory() {
    const [data, setData] = useState(null)
    const [searchTerm, setSearchTerm] = useState('')
    const [showFilters, setShowFilters] = useState(false)
    const [platformFilter, setPlatformFilter] = useState('')
    const [statusFilter, setStatusFilter] = useState('')
    const [expandedPurchaseKeys, setExpandedPurchaseKeys] = useState(new Set())
    const [paginatedRows, setPaginatedRows] = useState([])
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 25,
        totalCount: 0,
        totalPages: 0,
        hasNext: false,
        hasPrevious: false,
    })

    const GetAppStoreSubscriptionData = async () => {
        try {
            setData(null)
            const result = await WhichWeightAPI(
                'BILLING',
                'GET',
                '/user_app_store_server_notifications'
            )
            setData(result.data.data ?? [])
        } catch (error) {
            console.error('Error fetching app store subscription data:', error)
            setData([])
        }
    }

    const purchaseRowKey = (purchase, index) =>
        `${purchase.userId}-${purchase.platform}-${purchase.transactionId ?? purchase.purchaseToken ?? index}`

    const TogglePurchaseExpanded = (key) => {
        setExpandedPurchaseKeys((prev) => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })
    }

    const OpenAthleteProfileInNewTab = (userId) => {
        window.open(`/athlete/${userId}`, '_blank')
    }

    const HandlePageSizeChange = (newPageSize) => {
        setPagination((prev) => ({
            ...prev,
            pageSize: parseInt(newPageSize, 10),
            page: 1,
        }))
    }

    const filteredRows = useMemo(() => {
        if (data === null) return []

        let rows = data

        if (platformFilter !== '') {
            rows = rows.filter((row) => row.platform === platformFilter)
        }

        if (statusFilter !== '') {
            rows = rows.filter(
                (row) => GetPurchaseSubscriptionStatus(row) === statusFilter
            )
        }

        const normalizedSearchTerm = searchTerm.toLowerCase().trim()
        if (normalizedSearchTerm === '') return rows

        const targetedSearchKeyMap = {
            id: (row) => row.userId,
            email: (row) => row.email,
            platform: (row) => row.platform,
            transactionid: (row) => row.transactionId,
            originaltransactionid: (row) => {
                const latest = row.appStoreServerNotifications?.[0]
                if (!latest || row.platform !== 'ios') return ''
                return GetIosNotificationSummary(latest.payload).originalTransactionId
            },
            productid: (row) => {
                const latest = row.appStoreServerNotifications?.[0]
                if (!latest || row.platform !== 'ios') return ''
                return GetIosNotificationSummary(latest.payload).productId
            },
        }

        const colonIndex = normalizedSearchTerm.indexOf(':')
        if (colonIndex > 0) {
            const rawKey = normalizedSearchTerm.slice(0, colonIndex).trim()
            const rawValue = normalizedSearchTerm.slice(colonIndex + 1).trim()
            const targetValueGetter = targetedSearchKeyMap[rawKey]

            if (targetValueGetter !== undefined && rawValue !== '') {
                return rows.filter((row) =>
                    (targetValueGetter(row) ?? '')
                        .toString()
                        .toLowerCase()
                        .includes(rawValue)
                )
            }
        }

        return rows.filter((row) => {
            const latestNotification = row.appStoreServerNotifications?.[0]
            const iosSummary =
                latestNotification && row.platform === 'ios'
                    ? GetIosNotificationSummary(latestNotification.payload)
                    : null

            const searchableValues = [
                row.userId,
                row.firstName,
                row.lastName,
                row.email,
                row.platform,
                row.transactionId,
                row.purchaseToken,
                iosSummary?.originalTransactionId,
                iosSummary?.productId,
                iosSummary?.environment,
                row.appStoreServerNotifications?.length,
                GetPurchaseSubscriptionStatus(row),
            ]

            return searchableValues.some((value) =>
                (value ?? '').toString().toLowerCase().includes(normalizedSearchTerm)
            )
        })
    }, [data, searchTerm, platformFilter, statusFilter])

    useEffect(() => {
        setPagination((prev) => ({ ...prev, page: 1 }))
    }, [searchTerm, platformFilter, statusFilter])

    useEffect(() => {
        GetAppStoreSubscriptionData()
    }, [])

    useEffect(() => {
        if (filteredRows.length > 0) {
            const totalCount = filteredRows.length
            const totalPages = Math.ceil(totalCount / pagination.pageSize) || 1
            const currentPage = Math.min(pagination.page, totalPages || 1)
            const startIndex = (currentPage - 1) * pagination.pageSize
            const endIndex = startIndex + pagination.pageSize

            setPagination((prev) => ({
                ...prev,
                totalCount,
                totalPages,
                page: currentPage,
                hasNext: currentPage < totalPages,
                hasPrevious: currentPage > 1,
            }))
            setPaginatedRows(filteredRows.slice(startIndex, endIndex))
            return
        }

        setPagination((prev) => ({
            ...prev,
            totalCount: 0,
            totalPages: 0,
            page: 1,
            hasNext: false,
            hasPrevious: false,
        }))
        setPaginatedRows([])
    }, [filteredRows, pagination.page, pagination.pageSize])

    if (data === null) {
        return (
            <ScreenFrame limitWidth={false}>
                <div className="flex items-center gap-x-4">
                    <h1 className="text-2xl font-semibold text-gray-900 tracking-tight flex-grow">
                        App Store Subscriptions
                    </h1>
                    <div className="w-40">
                        <OutlineButton
                            text="Refresh"
                            action={GetAppStoreSubscriptionData}
                            className="shadow-md"
                        />
                    </div>
                </div>
                <div className="w-full h-20 bg-gray-200 rounded-lg animate-pulse" />
                <div className="w-full h-[600px] bg-gray-200 rounded-lg animate-pulse" />
            </ScreenFrame>
        )
    }

    return (
        <ScreenFrame limitWidth={false}>
            <div className="flex items-center gap-x-4">
                <h1 className="text-2xl font-semibold text-gray-900 tracking-tight flex-grow">
                    App Store Subscriptions
                </h1>
                <div className="w-40">
                    <OutlineButton
                        text="Refresh"
                        action={GetAppStoreSubscriptionData}
                        className="shadow-md"
                    />
                </div>
            </div>

            <p className="text-sm text-gray-600 mb-4">
                iOS and Android billing webhooks linked to users. Use{' '}
                <span className="font-mono text-xs bg-gray-100 px-1 rounded">
                    originalTransactionId
                </span>{' '}
                for App Store Connect trial extensions. Android RTDN payloads do not include
                price; use Play Console for billing amounts.
            </p>

            <div className="mb-4">
                <SearchBar
                    placeholder="Search or key:value (id, email, platform, transactionId, originalTransactionId, productId)"
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    showFilters={showFilters}
                    setShowFilters={setShowFilters}
                />
            </div>

            <div>
                <div
                    className="-mb-4 flex flex-wrap items-center gap-4 transition-all duration-200 select-none"
                    style={{
                        maxHeight: showFilters ? '120px' : '0px',
                        opacity: showFilters ? 1 : 0,
                        paddingBottom: showFilters ? '1rem' : '0',
                        pointerEvents: showFilters ? 'auto' : 'none',
                    }}
                >
                    <div className="flex items-center gap-x-1">
                        <p className="text-sm text-gray-700">Platform:</p>
                        <div className="border border-gray-200 rounded-md shadow-md bg-white">
                            <select
                                value={platformFilter}
                                className="px-2 py-1 text-sm border-r-[10px] border-white rounded-md cursor-pointer focus:outline-none"
                                onChange={(e) => setPlatformFilter(e.target.value)}
                            >
                                <option value="">All</option>
                                <option value="ios">iOS</option>
                                <option value="android">Android</option>
                            </select>
                        </div>
                    </div>

                    <div className="flex items-center gap-x-1">
                        <p className="text-sm text-gray-700">Status:</p>
                        <div className="border border-gray-200 rounded-md shadow-md bg-white">
                            <select
                                value={statusFilter}
                                className="px-2 py-1 text-sm border-r-[10px] border-white rounded-md cursor-pointer focus:outline-none"
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option value="">All</option>
                                <option value="active">Active</option>
                                <option value="trialing">Trialing</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>
                    </div>

                    <div className="flex items-center gap-x-1">
                        <p className="text-sm text-gray-700">Items per page:</p>
                        <div className="border border-gray-200 rounded-md shadow-md bg-white">
                            <select
                                value={pagination.pageSize}
                                className="px-2 py-1 text-sm border-r-[10px] border-white rounded-md cursor-pointer focus:outline-none"
                                onChange={(e) => HandlePageSizeChange(e.target.value)}
                            >
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            <PaginationController pagination={pagination} setPagination={setPagination} />

            {data.length === 0 && (
                <div className="p-4 text-gray-400 text-center">No app store subscription data</div>
            )}

            <div className="space-y-3">
                {paginatedRows.map((purchase, index) => {
                    const rowKey = purchaseRowKey(purchase, index)
                    const isExpanded = expandedPurchaseKeys.has(rowKey)
                    const notifications = purchase.appStoreServerNotifications ?? []
                    const latestNotification = notifications[0]
                    const latestIosSummary =
                        purchase.platform === 'ios' && latestNotification
                            ? GetIosNotificationSummary(latestNotification.payload)
                            : null
                    const latestEventLabel =
                        purchase.platform === 'ios' && latestIosSummary
                            ? latestIosSummary.eventLabel
                            : latestNotification && purchase.platform === 'android'
                              ? GetAndroidNotificationSummary(latestNotification.payload)
                                    .eventLabel
                              : null
                    const subscriptionStatus = GetPurchaseSubscriptionStatus(purchase)
                    const totalSubscriptionDollars =
                        GetPurchaseTotalSubscriptionDollars(purchase)

                    return (
                        <div
                            key={rowKey}
                            className="border border-gray-200 rounded-md overflow-hidden bg-white shadow-md"
                        >
                            <div className="grid grid-cols-[auto_1fr_auto_auto] gap-3 items-center px-3 py-2 bg-white border-b border-gray-200">
                                <div className="flex items-center gap-2 min-w-[88px]">
                                    {purchase.platform === 'ios' && (
                                        <img
                                            src={AppleAppStoreIcon}
                                            alt="iOS"
                                            className="w-5 h-5"
                                        />
                                    )}
                                    {purchase.platform === 'android' && (
                                        <img
                                            src={GooglePlayStoreIcon}
                                            alt="Android"
                                            className="w-5 h-5"
                                        />
                                    )}
                                    <span className="text-sm font-medium capitalize">
                                        {purchase.platform}
                                    </span>
                                </div>

                                <div className="min-w-0">
                                    <div className="text-sm font-medium text-gray-900 truncate">
                                        {[purchase.firstName, purchase.lastName]
                                            .filter(Boolean)
                                            .join(' ') || '—'}{' '}
                                        <span className="text-gray-500 font-normal">
                                            ({purchase.email})
                                        </span>
                                    </div>
                                    <div className="text-xs text-gray-600 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                                        <SubscriptionStatusTag status={subscriptionStatus} />
                                        <span>User {purchase.userId}</span>
                                        <span>
                                            Linked{' '}
                                            {FormatDbTimestamp(purchase.createdTimestamp)}
                                        </span>
                                        <span>
                                            {notifications.length} notification
                                            {notifications.length === 1 ? '' : 's'}
                                        </span>
                                        {latestEventLabel && (
                                            <span className="text-gray-800">
                                                Latest: {latestEventLabel}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="text-right shrink-0">
                                    {totalSubscriptionDollars.hasAmount && (
                                        <div
                                            title={`Sum of ${totalSubscriptionDollars.uniqueTransactionCount} unique App Store transactionId(s). Uses decodedTransactionInfo.price (actual charge per transaction), not webhook count.`}
                                        >
                                            <div className="text-xxs text-gray-500 uppercase tracking-wide">
                                                Total charged
                                            </div>
                                            <div className="text-sm font-semibold text-gray-900">
                                                {totalSubscriptionDollars.formatted}
                                            </div>
                                            {totalSubscriptionDollars.duplicateChargeWebhookCount >
                                                0 && (
                                                <div className="text-xxs text-amber-700 mt-0.5">
                                                    {totalSubscriptionDollars.chargeWebhookCount}{' '}
                                                    charge webhooks,{' '}
                                                    {
                                                        totalSubscriptionDollars.uniqueTransactionCount
                                                    }{' '}
                                                    unique txn
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="flex items-center gap-1 shrink-0">
                                    <button
                                        type="button"
                                        className="text-gray-400 hover:text-gray-700 p-1"
                                        title="Open athlete profile"
                                        onClick={() => OpenAthleteProfileInNewTab(purchase.userId)}
                                    >
                                        <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                                    </button>
                                    <button
                                        type="button"
                                        className="p-1 text-gray-500 hover:text-gray-800"
                                        onClick={() => TogglePurchaseExpanded(rowKey)}
                                        title={isExpanded ? 'Collapse' : 'Expand events'}
                                    >
                                        {isExpanded ? (
                                            <ChevronUpIcon className="h-5 w-5" />
                                        ) : (
                                            <ChevronDownIcon className="h-5 w-5" />
                                        )}
                                    </button>
                                </div>
                            </div>

                            {isExpanded && (
                                <div className="p-3 space-y-3 bg-gray-50 border-t border-gray-200">
                                    <div className="p-3 bg-white border border-gray-200 rounded-md shadow-sm">
                                        <div className="flex items-center justify-between mb-2">
                                            <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                                                Subscription identifiers
                                            </h3>
                                            <SubscriptionStatusTag status={subscriptionStatus} />
                                        </div>
                                        <PurchaseSummaryFields
                                            purchase={purchase}
                                            latestIosSummary={latestIosSummary}
                                        />
                                    </div>

                                    {notifications.length === 0 && (
                                        <p className="text-sm text-gray-500 text-center py-4">
                                            No webhook notifications matched this purchase link
                                            yet.
                                        </p>
                                    )}

                                    {notifications.length > 0 && (
                                        <div className="space-y-2">
                                            <div>
                                                <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                                                    Webhook events (newest first)
                                                </h3>
                                                {purchase.platform === 'ios' && (
                                                    <p className="text-xxs text-gray-500 mt-1">
                                                        Total charged sums unique Apple{' '}
                                                        <span className="font-mono">transactionId</span>
                                                        s (real charges). Duplicate SUBSCRIBED
                                                        webhooks with the same transaction are labeled
                                                        and not double-counted — common in sandbox
                                                        test data.
                                                    </p>
                                                )}
                                            </div>
                                            {(() => {
                                                const chargeDisplayMap =
                                                    purchase.platform === 'ios'
                                                        ? BuildIosChargeEventDisplayMap(
                                                              notifications
                                                          )
                                                        : new Map()

                                                return notifications.map(
                                                    (notification, nIndex) => (
                                                        <NotificationCard
                                                            key={notification.id}
                                                            notification={notification}
                                                            platform={purchase.platform}
                                                            defaultExpanded={nIndex === 0}
                                                            chargeDisplay={chargeDisplayMap.get(
                                                                notification.id
                                                            )}
                                                        />
                                                    )
                                                )
                                            })()}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>

            <PaginationController pagination={pagination} setPagination={setPagination} />
        </ScreenFrame>
    )
}
